import React, { useState, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import AsyncSelect from "react-select/async"; // ✅ AsyncSelect Import kiya
import { customFetch } from '../../apiClient';

const MediVendorDueReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // ✅ Naya state selected vendor ke liye
  const [selectedVendor, setSelectedVendor] = useState(null);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await customFetch(`/MediVendorDue/outstanding-list`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch vendor dues");
      }
      const data = await res.json();
      setReportData(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ AsyncSelect ke liye backend se vendors search karne ka function
  const loadVendorOptions = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) return []; // Kam se kam 2 letters type hone par search kare
    try {
      const res = await customFetch(`/VendorDT/search?name=${inputValue}`);
      if (!res.ok) return [];
      const data = await res.json();
      // Dropdown ke liye format map kar rahe hain (value aur label)
      return data.map(v => ({
        value: v.vid,
        label: `${v.vendorName} ${v.mobile ? `(${v.mobile})` : ''}`
      }));
    } catch (err) {
      console.error("Error fetching vendors:", err);
      return [];
    }
  };

  // ✅ Client-side Filtering & Summary Calculation
  const { filteredData, summary } = useMemo(() => {
    // 1. Agar koi vendor selected hai toh sirf uska data dikhao, warna saara dikhao
    const filtered = reportData.filter(row => {
      if (!selectedVendor) return true; 
      return row.vid === selectedVendor.value;
    });

    // 2. Filtered data ka total calculate karein
    let opBal = 0, purchased = 0, paid = 0, due = 0;
    
    filtered.forEach(row => {
      opBal     += Number(row.openingBalance || 0);
      purchased += Number(row.totalPurchased || 0);
      paid      += Number(row.totalPaid || 0);
      due       += Number(row.totalDue || 0);
    });

    return {
      filteredData: filtered,
      summary: { opBal, purchased, paid, due, vendorCount: filtered.length }
    };
  }, [reportData, selectedVendor]);

  // ✅ Excel Export
  const exportToExcel = useCallback(() => {
    if (!filteredData.length) { alert("No data to export!"); return; }
    
    const exportFormat = filteredData.map((row, index) => ({
      "S.No": index + 1,
      "Vendor Name": row.vendorName,
      "Mobile": row.mobile || "-",
      "Opening Balance": row.openingBalance,
      "Total Purchased": row.totalPurchased,
      "Total Paid": row.totalPaid,
      "Total Due (Outstanding)": row.totalDue
    }));

    const ws = XLSX.utils.json_to_sheet(exportFormat);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vendor Dues");
    XLSX.writeFile(wb, `Vendor_Outstanding_Report.xlsx`);
  }, [filteredData]);

  // ✅ AsyncSelect ke design ko thoda clean karne ke liye custom styles
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      fontSize: '12px',
      borderRadius: '4px',
      borderColor: '#cbd5e1',
      boxShadow: 'none',
      '&:hover': { borderColor: '#94a3b8' }
    }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    input: (base) => ({ ...base, margin: '0', padding: '0' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, padding: '4px' }),
    option: (base) => ({ ...base, fontSize: '12px' })
  };

  return (
    <div style={{ padding: "0", fontSize: "12px" }}>
      <div style={{
        background: "white", borderRadius: "8px",
        boxShadow: "0 1px 10px rgba(0,0,0,0.06)",
        border: "1px solid #e0e6ed", overflow: "hidden"
      }}>

        {/* ── Header ── */}
        <div style={{
          background: "#6366f1",
          padding: "10px 16px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "white" }}>
            <div style={{
              width: 34, height: 34, background: "rgba(255,255,255,0.2)",
              borderRadius: 6, display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: 16
            }}>
              <i className="bi bi-people-fill"></i>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Vendor Outstanding Report</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>
                Real-time payable balances and purchase history
              </div>
            </div>
          </div>
          <button
            onClick={exportToExcel}
            disabled={!filteredData.length}
            style={{
              padding: "5px 14px", fontSize: 11, fontWeight: 600,
              background: "white", border: "none", borderRadius: 4,
              cursor: filteredData.length ? "pointer" : "not-allowed",
              opacity: filteredData.length ? 1 : 0.5, color: "#1e293b"
            }}
          >
            <i className="bi bi-file-earmark-excel text-success me-1"></i>
            Export Excel
          </button>
        </div>

        <div style={{ padding: 14 }}>

          {/* ── Error ── */}
          {error && (
            <div style={{
              padding: "8px 12px", background: "#fee2e2", color: "#dc2626",
              borderRadius: 6, marginBottom: 10, fontSize: 12
            }}>
              ⚠️ {error}
            </div>
          )}

          {/* ── Filter Bar (Async Select & Refresh) ── */}
          <div style={{
            display: "flex", gap: 8, background: "#f8fafc",
            padding: "10px 12px", borderRadius: 6,
            border: "1px solid #e0e6ed", marginBottom: 12,
            alignItems: "center", flexWrap: "wrap", justifyContent: "space-between"
          }}>
            
            {/* ✅ React AsyncSelect yahan lagaya hai */}
            <div style={{ flex: 1, minWidth: "250px", maxWidth: "400px" }}>
              <label style={{ fontSize: 11, fontWeight: 600, marginBottom: 4, color: "#475569", display: "block" }}>
                Search Vendor
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions={false}
                loadOptions={loadVendorOptions}
                value={selectedVendor}
                onChange={(option) => setSelectedVendor(option)}
                placeholder="Type vendor name to search..."
                isClearable={true}
                styles={selectStyles}
                noOptionsMessage={({ inputValue }) => 
                  !inputValue ? "Type to search..." : "No vendor found"
                }
              />
            </div>

            <button
              onClick={fetchReport} disabled={loading}
              style={{
                padding: "0 18px", height: 32, marginTop: "18px", // Margin top taaki label ke sath align ho
                background: "white",
                color: "#6366f1", border: "1px solid #6366f1", borderRadius: 4,
                fontWeight: 600, cursor: loading ? "not-allowed" : "pointer",
                fontSize: 12, opacity: loading ? 0.7 : 1, display: "flex", alignItems: "center", gap: 5
              }}
            >
              <i className={`bi bi-arrow-repeat ${loading ? "fa-spin" : ""}`}></i>
              {loading ? "Refreshing..." : "Refresh Data"}
            </button>
          </div>

          {/* ── Summary Cards ── */}
          {!loading && reportData.length > 0 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))",
              gap: 10, marginBottom: 14
            }}>
              {[
                {
                  label: selectedVendor ? "Selected Vendor" : "Total Vendors",
                  val: selectedVendor ? 1 : summary.vendorCount,
                  bg: "linear-gradient(135deg,#64748b,#475569)"
                },
                {
                  label: "Total Purchased",
                  val: `₹ ${summary.purchased.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#3b82f6,#2563eb)"
                },
                {
                  label: "Total Paid",
                  val: `₹ ${summary.paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#10b981,#059669)"
                },
                {
                  label: "Total Outstanding (Due)",
                  val: `₹ ${summary.due.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`,
                  bg: "linear-gradient(135deg,#ef4444,#dc2626)"
                },
              ].map((card, i) => (
                <div key={i} style={{
                  padding: "12px 14px", borderRadius: 8, color: "white",
                  background: card.bg, boxShadow: "0 2px 6px rgba(0,0,0,0.12)"
                }}>
                  <div style={{ fontSize: 10, opacity: 0.9, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                    {card.label}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, marginTop: 3 }}>{card.val}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Data Table ── */}
          <div style={{
            border: "1px solid #e0e6ed", borderRadius: 6, overflow: "hidden"
          }}>
            <div style={{ overflowX: "auto", maxHeight: 480 }}>
              <table style={{
                width: "100%", borderCollapse: "collapse", fontSize: 12
              }}>
                <thead>
                  <tr>
                    <th style={thStyle("#f8fafc", "center")}>#</th>
                    <th style={thStyle("#f8fafc", "left")}>Vendor Name</th>
                    <th style={thStyle("#f8fafc", "center")}>Mobile</th>
                    <th style={thStyle("#f8fafc", "right")}>Opening Bal.</th>
                    <th style={thStyle("#f8fafc", "right")}>Purchased</th>
                    <th style={thStyle("#f8fafc", "right")}>Paid</th>
                    <th style={thStyle("#f8fafc", "right")}>Total Due</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
                        <i className="bi bi-hourglass-split me-2"></i> Fetching Data...
                      </td>
                    </tr>
                  ) : filteredData.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}>
                        <i className="bi bi-inbox me-2"></i> No records match your search.
                      </td>
                    </tr>
                  ) : (
                    filteredData.map((row, ri) => (
                      <tr
                        key={row.vid}
                        style={{ background: ri % 2 === 0 ? "white" : "#fafafa" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f9ff"}
                        onMouseLeave={e => e.currentTarget.style.background = ri % 2 === 0 ? "white" : "#fafafa"}
                      >
                        <td style={tdStyle("center", true)}>{ri + 1}</td>
                        <td style={{...tdStyle("left", false), fontWeight: 600, color: "#2563eb"}}>
                          {row.vendorName}
                        </td>
                        <td style={tdStyle("center", false)}>{row.mobile || "—"}</td>
                        
                        <td style={tdStyle("right", false)}>
                          {row.openingBalance.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{...tdStyle("right", false), color: "#0ea5e9", fontWeight: 600}}>
                          {row.totalPurchased.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{...tdStyle("right", false), color: "#10b981", fontWeight: 600}}>
                          {row.totalPaid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{
                          ...tdStyle("right", false), 
                          fontWeight: 700, 
                          color: row.totalDue > 0 ? "#ef4444" : (row.totalDue < 0 ? "#10b981" : "#1e293b")
                        }}>
                          {row.totalDue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* ── Footer Totals ── */}
                {filteredData.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#1e293b", color: "white" }}>
                      <td colSpan={3} style={{ padding: "7px 10px", fontWeight: 700, textAlign: "right" }}>
                        {selectedVendor ? "Total for Selected Vendor:" : "Grand Total (All Vendors):"}
                      </td>
                      <td style={{ padding: "7px 10px", fontWeight: 700, textAlign: "right" }}>
                        {summary.opBal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "7px 10px", fontWeight: 700, textAlign: "right" }}>
                        {summary.purchased.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "7px 10px", fontWeight: 700, textAlign: "right" }}>
                        {summary.paid.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{ padding: "7px 10px", fontWeight: 700, textAlign: "right", color: "#fca5a5" }}>
                        {summary.due.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

// ── Style helpers ──
const thStyle = (bg, align) => ({
  background: bg, color: "#334155",
  padding: "8px 10px", fontWeight: 600,
  borderBottom: "2px solid #e2e8f0",
  textAlign: align, whiteSpace: "nowrap",
  position: "sticky", top: 0, zIndex: 1,
  fontSize: 12
});

const tdStyle = (align, muted) => ({
  padding: "6px 10px",
  borderBottom: "1px solid #f1f5f9",
  textAlign: align,
  color: muted ? "#64748b" : "#1e293b",
  verticalAlign: "middle"
});

export default MediVendorDueReport;